// What a user can reach, on paper — the list-and-print half of User Access.
//
// The four transfer panels answer "what is allotted" one level at a time and
// only for the row that happens to be selected. The question that gets asked
// away from the screen is the whole of it: hand the customer, or the installer,
// a sheet that says which devices are in which room of which location, with the
// unique numbers on it. That is what this builds.
//
// ── One report model, four scopes ──────────────────────────────────────────
// Every panel prints WHAT ITS ALLOTTED LIST HOLDS, expanded downwards:
//
//   Locations   -> every location this user has, its rooms, and their devices
//   Rooms       -> the selected location: rooms summary, common, per-room lists
//   Devices     -> the selected room's devices
//   Common      -> the selected location's roomless devices
//
// so the four buttons are the same drill-down the panels already are, and the
// device columns never change: Sr. No. / Device Name / Unique Number /
// Device Type Name.
//
// ⚠️ THE SHEET IS BUILT FROM THE SAME LOADERS THE PANELS USE, and that is not
// an accident of convenience. `GetDeviceListsbyUserIDandLocationId` would answer
// for a whole location in ONE request instead of one per room — but it drops
// DeviceIDs 3, 19 and 21, and it is a different question (device mapping, not
// room mapping). A printed list that quietly disagrees with the screen it was
// printed from is worse than no list, because the operator trusts the paper. So
// the report pays the extra requests and reads exactly what the panels read.
//
// ⚠️ NOTHING HERE RE-SORTS. The allotted arrays arrive in the customer's own
// SortOrder (userAccess.annotateAllotted keeps the allotted reply's order, which
// is where SortOrder lives) and that order is what the panel draws and what the
// arrows write. Sorting again — even by the same rule — would let the sheet and
// the screen disagree the moment one of the three un-ordered replies noted in
// core/utils/sortOrder is in play.

import { loadRooms, loadRoomDevices, loadCommonDevices } from './userAccess';
import { userLabel } from '../devices/deviceAdmin';
import { deviceCatalogEntry } from '../devices/deviceCatalog';
import { deviceLabel } from '../devices/deviceMeta';

// ── Columns ────────────────────────────────────────────────────────────────
//
// ⚠️ PERCENTAGES, AND EACH SET SUMS TO 100. These used to be millimetres, which
// is a PAPER unit: the on-screen table had no widths at all and sized its
// columns by content, so the preview and the printed sheet laid the same data
// out differently. A percentage is of whatever it is in — 190mm of A4, or the
// width of a dialog — so one set of numbers now drives both. Both tables are
// `table-layout: fixed`, so these are binding rather than advisory.

export const DEVICE_COLUMNS = [
  { key: 'sr', label: 'Sr. No.', width: '9%', align: 'right' },
  { key: 'name', label: 'Device Name', width: '40%' },
  { key: 'uno', label: 'Unique Number', width: '24%', mono: true },
  { key: 'type', label: 'Device Type Name', width: '27%' },
];

export const ROOM_COLUMNS = [
  { key: 'sr', label: 'Sr. No.', width: '9%', align: 'right' },
  { key: 'name', label: 'Room Name', width: '55%' },
  { key: 'id', label: 'Room ID', width: '18%', align: 'right' },
  { key: 'count', label: 'Devices', width: '18%', align: 'right' },
];

/*
 * The flat sheet the spreadsheet gets.
 *
 * ⚠️ NOT THE SAME SHAPE AS THE PRINTED ONE, deliberately. On paper the room is a
 * HEADING and the four asked-for columns sit under it; in a spreadsheet a
 * heading row is the thing that breaks sorting, filtering and every pivot — so
 * the location and room become COLUMNS and the whole export is one table you
 * can filter. The header block is not lost: it is the second sheet.
 */
export const EXCEL_COLUMNS = [
  { key: 'sr', label: 'Sr. No.', width: 8, type: 'number' },
  { key: 'location', label: 'Location Name', width: 26 },
  { key: 'room', label: 'Room Name', width: 22 },
  { key: 'name', label: 'Device Name', width: 30 },
  { key: 'uno', label: 'Unique Number', width: 20 },
  { key: 'type', label: 'Device Type Name', width: 28 },
];

// What goes in the Room column for a device that belongs to the location. The
// brackets matter: a bare "Common" sorts and filters as if it were a room
// somebody named Common, and one location in this estate very nearly has one.
export const COMMON_ROOM = '(Common)';

export const LOCATION_COLUMNS = [
  { key: 'sr', label: 'Sr. No.', width: '8%', align: 'right' },
  { key: 'name', label: 'Location Name', width: '44%' },
  { key: 'id', label: 'Location ID', width: '18%', align: 'right' },
  { key: 'rooms', label: 'Rooms', width: '15%', align: 'right' },
  { key: 'count', label: 'Devices', width: '15%', align: 'right' },
];

// ── Naming ─────────────────────────────────────────────────────────────────

const text = (v) => String(v ?? '').trim();

/**
 * The location's name, exactly as the Locations panel labels it.
 *
 * `DisplayName` is GetAllLocations' owner-qualified "<owner>-<name>" — on a list
 * where "Home" appears dozens of times it is the only one that names anything.
 */
export const locationName = (l) => (l
  ? text(l.DisplayName) || text(l.LocationName) || `Location ${l.LocationID}`
  : '');

export const roomName = (r) => (r
  ? text(r.RoomName) || `Room ${r.RoomID}`
  : '');

/**
 * The customer's name for the device — the SAME column the panel draws.
 *
 * ⚠️ Not `ActualDeviceName`. Two replies in this app name their columns the
 * other way round (see core/devices/favouriteNames), but none of the four this
 * page loads from is one of them, and reading a column the panel does not read
 * is how a sheet ends up disagreeing with the screen beside it.
 */
export const deviceName = (d) => (d
  ? text(d.DeviceName) || text(d.UniqueNumber) || '(unnamed)'
  : '');

/**
 * The PRODUCT name — "4 Touch Switch", "Curtain Switch".
 *
 * Four sources, in falling order of directness, because no single one answers
 * for every row: the per-user room replies carry `DeviceTypeName` and the
 * unfiltered ones do not; the backend's own DeviceTypes catalogue covers
 * anything with a DeviceID; `deviceLabel` is the app's hand-written table, which
 * still knows products on a phone whose catalogue fetch has not landed yet; and
 * `DeviceType` is the CLASSIFICATION ("Switch", "Sensor") — a poor name, but
 * better than an empty cell on a sheet someone has to work from.
 */
export function deviceTypeName(d) {
  if (!d) return '';
  const given = text(d.DeviceTypeName);
  if (given) return given;

  const id = Number(d.DeviceID ?? d.DeviceId);
  if (Number.isFinite(id) && id) {
    const entry = deviceCatalogEntry(id);
    if (entry && entry.name) return entry.name;
    const label = deviceLabel(id);
    // 'Device' is deviceLabel's "I do not know" — printing it as a product name
    // states something the app has not established.
    if (label && label !== 'Device') return label;
  }
  return text(d.DeviceType) || '—';
}

// ── Rows ───────────────────────────────────────────────────────────────────

/** Devices, numbered from 1 — Sr. No. restarts in every section. */
export const deviceRows = (devices) => (devices || []).map((d, i) => ({
  sr: i + 1,
  name: deviceName(d),
  uno: text(d.UniqueNumber),
  type: deviceTypeName(d),
}));

/**
 * A section of devices — the only kind the Excel sheet flattens.
 *
 * ⚠️ `scope` IS CARRIED, NOT RECOVERED FROM THE TITLE. The four builders title
 * their sections differently ("Bedroom" in one, "Patel-Home — Bedroom" in
 * another, "Common Devices" in a third), so a flattener that split the heading
 * on an em dash would put half a location name in the Room column the first
 * time a customer named a room "Front — Left". The location and room are known
 * at the moment the section is built; they are simply kept.
 */
const deviceSection = ({
  title, subtitle, devices, empty, location, room,
}) => ({
  kind: 'devices',
  title,
  subtitle,
  scope: { location: location || '', room: room || '' },
  columns: DEVICE_COLUMNS,
  rows: deviceRows(devices),
  empty,
});

const countDevices = (loc) => (loc.common || []).length
  + (loc.rooms || []).reduce((n, r) => n + (r.devices || []).length, 0);

const roomRows = (rooms) => (rooms || []).map((r, i) => ({
  sr: i + 1,
  name: roomName(r.room),
  id: text(r.room && r.room.RoomID),
  count: (r.devices || []).length,
}));

const locationRows = (locations) => (locations || []).map((l, i) => ({
  sr: i + 1,
  name: locationName(l.location),
  id: text(l.location && l.location.LocationID),
  rooms: (l.rooms || []).length,
  count: countDevices(l),
}));

// ── Header ─────────────────────────────────────────────────────────────────

/**
 * The stamp on the sheet.
 *
 * Taken as an argument so the builders stay pure and testable — the caller
 * passes `Date.now()`. This is one of the few timestamps in the app that is NOT
 * a server value, so it needs none of the IST correction every device stamp
 * goes through (see core/devices/firmwareInfo.parseServerTime).
 */
export const formatStamp = (at) => new Date(at)
  .toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });

const userMeta = (user) => {
  if (!user) return [];
  const bits = [`ID ${user.UserId}`];
  if (user.MobileNumber) bits.push(String(user.MobileNumber));
  return [{ label: 'User', value: `${userLabel(user)} · ${bits.join(' · ')}` }];
};

const meta = (user, rows, at) => [
  ...userMeta(user),
  ...rows.filter(Boolean),
  { label: 'Generated', value: formatStamp(at) },
];

const locationMeta = (location) => ({
  label: 'Location',
  value: location
    ? `${locationName(location)} · ID ${location.LocationID}`
    : '—',
});

// ── The four reports ───────────────────────────────────────────────────────
//
// All pure. Each returns { title, meta[], sections[], total } and knows nothing
// about how it will be drawn — the same model feeds the on-screen list and the
// printed sheet, so the two cannot drift.

/** One room's allotted devices. */
export function roomDeviceReport({
  user, location, room, devices, at = Date.now(),
}) {
  const rows = deviceRows(devices);
  return {
    title: `Devices — ${roomName(room)}`,
    meta: meta(user, [
      locationMeta(location),
      { label: 'Room', value: room ? `${roomName(room)} · ID ${room.RoomID}` : '—' },
      { label: 'Devices', value: String(rows.length) },
    ], at),
    sections: [deviceSection({
      title: roomName(room) || 'Devices',
      devices,
      location: locationName(location),
      room: roomName(room),
      empty: 'No devices allotted in this room.',
    })],
    total: rows.length,
  };
}

/** A location's common devices — the ones that belong to no room. */
export function commonDeviceReport({
  user, location, devices, at = Date.now(),
}) {
  const rows = deviceRows(devices);
  return {
    title: `Common Devices — ${locationName(location)}`,
    meta: meta(user, [
      locationMeta(location),
      { label: 'Room', value: 'Common — belongs to the location, not to any room' },
      { label: 'Devices', value: String(rows.length) },
    ], at),
    sections: [deviceSection({
      title: 'Common Devices',
      devices,
      location: locationName(location),
      room: COMMON_ROOM,
      empty: 'No common devices allotted at this location.',
    })],
    total: rows.length,
  };
}

/**
 * One location in full: a rooms summary, the common devices, then every room.
 *
 * `rooms` is [{ room, devices }] in the panel's order — see gatherLocation.
 */
export function locationReport({
  user, location, rooms = [], common = [], at = Date.now(),
}) {
  const total = countDevices({ rooms, common });
  const sections = [{
    title: 'Rooms',
    subtitle: 'Allotted to this user at this location',
    columns: ROOM_COLUMNS,
    rows: roomRows(rooms),
    empty: 'No rooms allotted at this location.',
  }];

  // The common devices lead, because they belong to the location itself —
  // printed after fourteen rooms they read as an afterthought, and they are
  // usually the gate and the water pump.
  if (common.length) {
    sections.push(deviceSection({
      title: 'Common Devices',
      subtitle: 'Belong to the location, not to any room',
      devices: common,
      location: locationName(location),
      room: COMMON_ROOM,
    }));
  }

  rooms.forEach((r) => {
    sections.push(deviceSection({
      title: roomName(r.room),
      subtitle: `Room ID ${(r.room && r.room.RoomID) ?? '—'}`,
      devices: r.devices,
      location: locationName(location),
      room: roomName(r.room),
      empty: 'No devices allotted in this room.',
    }));
  });

  return {
    title: `Access Report — ${locationName(location)}`,
    meta: meta(user, [
      locationMeta(location),
      { label: 'Rooms', value: String(rooms.length) },
      { label: 'Devices', value: `${total} (${common.length} common)` },
    ], at),
    sections,
    total,
  };
}

/**
 * Everything this user can reach, across every allotted location.
 *
 * `locations` is [{ location, rooms, common }] — see gatherUser.
 */
export function userAccessReport({ user, locations = [], at = Date.now() }) {
  const total = locations.reduce((n, l) => n + countDevices(l), 0);
  const rooms = locations.reduce((n, l) => n + (l.rooms || []).length, 0);

  const sections = [{
    title: 'Locations',
    subtitle: 'Allotted to this user',
    columns: LOCATION_COLUMNS,
    rows: locationRows(locations),
    empty: 'No locations allotted to this user.',
  }];

  locations.forEach((l) => {
    const name = locationName(l.location);
    if ((l.common || []).length) {
      sections.push(deviceSection({
        title: `${name} — Common Devices`,
        subtitle: 'Belong to the location, not to any room',
        devices: l.common,
        location: name,
        room: COMMON_ROOM,
      }));
    }
    (l.rooms || []).forEach((r) => {
      sections.push(deviceSection({
        title: `${name} — ${roomName(r.room)}`,
        subtitle: `Room ID ${(r.room && r.room.RoomID) ?? '—'}`,
        devices: r.devices,
        location: name,
        room: roomName(r.room),
        empty: 'No devices allotted in this room.',
      }));
    });
  });

  return {
    title: 'User Access Report',
    meta: meta(user, [
      { label: 'Locations', value: String(locations.length) },
      { label: 'Rooms', value: String(rooms) },
      { label: 'Devices', value: String(total) },
    ], at),
    sections,
    total,
  };
}

// ── The spreadsheet ────────────────────────────────────────────────────────
//
// Built from the SAME report model the screen and the printer use, so all three
// answer for the same rows. Only the shape differs — see EXCEL_COLUMNS.

/**
 * Every device in the report as one flat table.
 *
 * ⚠️ Sr. No. RUNS RIGHT THROUGH, unlike the printed sheet where it restarts in
 * every room. A number that restarts is meaningful under a heading and
 * meaningless in a filterable table — sort by Device Name and a per-room
 * counter becomes noise, while a continuous one still identifies the row.
 *
 * The summary sections (Rooms, Locations) are left out: they are counts of the
 * rows below them, and a spreadsheet can produce those itself.
 */
export function flatDeviceRows(report) {
  const out = [];
  ((report && report.sections) || []).forEach((s) => {
    if (s.kind !== 'devices') return;
    const scope = s.scope || {};
    (s.rows || []).forEach((r) => {
      out.push({
        // Scope first, so a row that carries its OWN location and room wins.
        // The access reports keep them on the section (one room per section);
        // the installed inventory keeps them on the row (one flat table). Both
        // flatten through here.
        location: scope.location || '',
        room: scope.room || '',
        ...r,
        // Always last: whatever numbering the section did, the flat table runs
        // straight through.
        sr: out.length + 1,
      });
    });
  });
  return out;
}

/**
 * The report as workbook sheets — see core/utils/xlsx.buildWorkbook.
 *
 * Two sheets, because the header block and the table want opposite things: the
 * table needs row 1 to be its column headers (that is what autofilter and the
 * frozen pane key on), and the header block needs to be somewhere it cannot be
 * sorted into the middle of the data.
 */
export function reportToSheets(report) {
  return [
    {
      name: 'Devices',
      // A report may bring its own column set — the installed inventory carries
      // a Status column these four do not. Defaulted, not required.
      columns: (report && report.excelColumns) || EXCEL_COLUMNS,
      rows: flatDeviceRows(report),
    },
    {
      name: 'Report',
      columns: [
        { key: 'field', label: 'Field', width: 22 },
        { key: 'value', label: 'Value', width: 60 },
      ],
      rows: [
        { field: 'Report', value: (report && report.title) || '' },
        ...((report && report.meta) || []).map((m) => ({ field: m.label, value: m.value })),
        { field: 'Total devices', value: String((report && report.total) ?? 0) },
      ],
      autoFilter: false,
      freezeHeader: false,
    },
  ];
}

const pad2 = (n) => String(n).padStart(2, '0');

/**
 * A file name for the workbook — the report's own title plus the date.
 *
 * ⚠️ Windows rejects \ / : * ? " < > | outright, and a location can be named
 * anything at all. The em dash in "Access Report — Home" is legal and kept.
 */
export function reportFileName(report, at = Date.now()) {
  const d = new Date(at);
  const stamp = `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
  const base = String((report && report.title) || 'Report')
    .replace(/[\\/:*?"<>|]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim() || 'Report';
  return `${base} ${stamp}.xlsx`;
}

// ── Gathering ──────────────────────────────────────────────────────────────

/**
 * Run `fn` over `items`, at most `limit` at a time.
 *
 * ⚠️ NOT `Promise.all`. One location can hold fourteen rooms and each room load
 * is itself two requests, so an unbounded fan-out queues thirty-odd against the
 * browser's six connections per host. Nothing here is timing-sensitive the way
 * the liveness reads in core/devices/firmwareUpdate are — but the page stays
 * live while this runs, and starving its own refreshes is how a report ends up
 * blamed for a panel that "stopped responding".
 */
export async function mapLimit(items, limit, fn) {
  const list = items || [];
  const out = new Array(list.length);
  let next = 0;
  const worker = async () => {
    for (;;) {
      const i = next;
      next += 1;
      if (i >= list.length) return;
      // eslint-disable-next-line no-await-in-loop
      out[i] = await fn(list[i], i);
    }
  };
  await Promise.all(
    Array.from({ length: Math.min(limit, list.length) }, worker),
  );
  return out;
}

const GATHER_LIMIT = 4;

/**
 * One location's allotted rooms, each with its allotted devices, plus the
 * location's common devices.
 *
 * `onProgress({ done, total, label })` is called as rooms land, because on a
 * large site this is thirty requests and a dialog that says only "Loading…"
 * looks hung.
 */
export async function gatherLocation(userId, location, onProgress) {
  const locationId = location && location.LocationID;
  const [roomsRes, common] = await Promise.all([
    loadRooms(userId, locationId).catch(() => ({ available: [], assigned: [] })),
    loadCommonDevices(userId, locationId)
      .then((r) => r.assigned).catch(() => []),
  ]);

  const list = roomsRes.assigned || [];
  let done = 0;
  const rooms = await mapLimit(list, GATHER_LIMIT, async (room) => {
    // A room that fails to load is reported as EMPTY, not dropped: a room the
    // user can open is part of their access whether or not its device list
    // answered, and omitting it understates what they can reach.
    const devices = await loadRoomDevices(userId, locationId, room.RoomID)
      .then((r) => r.assigned).catch(() => []);
    done += 1;
    if (onProgress) onProgress({ done, total: list.length, label: roomName(room) });
    return { room, devices };
  });

  return { location, rooms, common };
}

/** Every allotted location, gathered the same way. */
export async function gatherUser(userId, locations, onProgress) {
  const list = locations || [];
  const out = [];
  // Sequential across locations, concurrent within one: the inner fan-out is
  // already at the connection limit, so overlapping two locations would only
  // make the progress line lie about which one is being read.
  for (let i = 0; i < list.length; i += 1) {
    const location = list[i];
    if (onProgress) {
      onProgress({ done: i, total: list.length, label: locationName(location) });
    }
    // eslint-disable-next-line no-await-in-loop
    out.push(await gatherLocation(userId, location, (p) => {
      if (!onProgress) return;
      onProgress({
        done: i,
        total: list.length,
        label: `${locationName(location)} — ${p.label} (${p.done}/${p.total})`,
      });
    }));
  }
  return out;
}
